import React, { useState, useEffect } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';

export interface ChartDataItem {
    day: string;
    orders: number;
    revenue: number;
}

interface Props {
    chartData: ChartDataItem[];
}

export const DashboardChart: React.FC<Props> = ({ chartData }) => {
    const [activePayload, setActivePayload] = useState<any>(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkSize = () => setIsMobile(window.innerWidth < 768);
        checkSize();
        window.addEventListener('resize', checkSize);
        return () => window.removeEventListener('resize', checkSize);
    }, []);

    const formatCurrency = (val: number) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

    return (
        <div className="w-full flex flex-col">
            {/* Dynamic Data Header - Solves the "thumb covering tooltip" mobile issue */}
            <div className="flex justify-between items-center mb-4 min-h-[48px] px-1">
                <div>
                    <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">
                        {activePayload ? activePayload[0].payload.day : 'Last 7 Days Avg'}
                    </p>
                    <p className="text-lg font-bold">
                        {activePayload
                            ? formatCurrency(activePayload.find((p: any) => p.dataKey === 'revenue')?.value)
                            : formatCurrency(chartData.reduce((a, b) => a + b.revenue, 0) / (chartData.length || 1))
                        }
                    </p>
                </div>
                {activePayload && (
                    <div className="text-right">
                        <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">Orders</p>
                        <p className="text-lg font-bold text-indigo-600">
                            {activePayload.find((p: any) => p.dataKey === 'orders')?.value}
                        </p>
                    </div>
                )}
            </div>

            {/* The Chart - Height is controlled here to prevent overlapping */}
            <div className="w-full h-[250px] md:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        onMouseMove={(e) => setActivePayload(e.activePayload)}
                        onMouseLeave={() => setActivePayload(null)}
                        margin={{ top: 5, right: 5, left: isMobile ? -35 : 0, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis
                            dataKey="day"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11 }}
                            dy={10}
                        />
                        <YAxis
                            hide={isMobile}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11 }}
                        />
                        {/* Custom Tooltip is null because we use the header above */}
                        <Tooltip content={() => null} cursor={{ stroke: '#6366f1', strokeWidth: 2 }} />
                        <Line
                            type="monotone"
                            dataKey="revenue"
                            stroke="#6366f1"
                            strokeWidth={3}
                            dot={false}
                            activeDot={{ r: 6, strokeWidth: 0 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="orders"
                            stroke="#10b981"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};